import {
  EncompassPointCloud,
  PointCloud,
  PotreePointCloud,
  GenericPointCloud,
  StreamablePointCloud,
  StreamablePointCloudNode
} from ".";
import {
  assignPackedData,
  PointCloudMaterial,
  PointCloudMaterialParams
} from "../rendering";
import {Viewer} from "../main";
import {
  boundsToMapLayer,
  closestPowerOfTwo,
  getProjFactor,
  mergeBoundingBoxes
} from "../utilities";
import { pointInPolygon } from "../utilities";
import {
  AmbientLight,
  BufferGeometry,
  Frustum,
  MathUtils,
  Matrix4,
  Points,
  Scene,
  Vector2,
  Vector3
} from "three";
import { Vector as VectorLayer } from "ol/layer";
import { isStaticSite } from "../../../electron-modules";
import LocalScene, { getEulerInverse } from "../projections";
import slash from "slash";
import { AssetType } from "../../../redux/assets-slice";
import { BinaryHeap } from "../binary-heap";

export class PointCloudManager {
  public viewer: Viewer;
  public scene: Scene;
  private layer: VectorLayer<any>;
  public materialDefaults: PointCloudMaterialParams;
  public clouds: (GenericPointCloud | StreamablePointCloud)[] = [];
  public visiblePointsTarget = 0;
  private maxUnloadedNodes = 2;
  private unloadedGeometry = [];
  private maxUnloadedClouds = 2;
  private maxloadedToGPUPerFrame = 2;
  private loadedToGPUThisFrame = 0;
  private nodeCacheSize = 500;
  private exeLaunchTimeout = 1000;
  private lastExeLaunched = performance.now();
  public numLoadedPoints = 0;
  public numVisiblePoints = 0;
  public numSceneNodesVisible = 0;
  private minPixelPercent = 0.20;
  public boundsVisible = false;
  public transparency = 1.0;
  public rawJsonResponse = [];
  public shouldUpdate = true;



  constructor(parent: Viewer) {
    this.viewer = parent;

    // Material defaults
    this.materialDefaults = {
      size: 1.0,
      minSize: 2.0,
      maxSize: 50.0,
      colorMap: "heat-map",
      colorType: 2,
      distance: this.viewer.far,
      dynamicSize: true,
      circularPoints: true,
      classifications: {},
      heightClamp: new Vector2(0, 1),
      intensityClamp: new Vector2(0, 1),
    };

    this.initScene();
  }

  get cameraList() {
    return this.viewer.cameraList;
  }

  get minimap() {
    return this.viewer.minimap;
  }

  get controls() {
    return this.viewer.controls;
  }

  get uniqueClassifications(): number[] {
    let uniqueClassifications = new Set();
    this.clouds.forEach(cloud => {
      cloud.uniqueClassifications.forEach((item: number) => {
        uniqueClassifications.add(item);
      });
    });
    return Array.from(uniqueClassifications) as number[];
  }

  get combinedBoundingBox() {
    const boundingBoxes = this.clouds.map(cloud => {
      return cloud.visible
        ? cloud.tightBoundingBox
        : null;
    });

    return mergeBoundingBoxes(boundingBoxes);
  }

  get genericPointClouds() {
    return this.clouds.filter(x => x instanceof GenericPointCloud) as GenericPointCloud[];
  }

  get streamingPointClouds() {
    return this.clouds.filter(x => x instanceof StreamablePointCloud) as StreamablePointCloud[];
  }

  get isEmpty() {
    return this.clouds.length === 0;
  }

  reconcile(pointClouds) {
    const currentPointClouds = new Set(this.clouds.map(cloud => cloud.identifier));
    const newPointClouds = new Set(pointClouds.map(cloud => cloud.id));

    const pointCloudsToLoad = pointClouds.filter(cloud => !currentPointClouds.has(cloud.id));
    const pointCloudsToDelete = this.clouds.filter(cloud => !newPointClouds.has(cloud.identifier));

    const numCloudsRemoved = pointCloudsToDelete.length;
    const numCloudsAdded = pointCloudsToLoad.length;

    // Delete clouds that need to be removed
    pointCloudsToDelete.forEach(pointCloud => {
      this.deletePointCloud(pointCloud.identifier);
    });

    // Add new pointclouds
    pointCloudsToLoad.forEach(pointCloud => {
      const cloudDetails = {
        name: pointCloud.name,
        identifier: pointCloud.id,
        path: slash(pointCloud.path),
        visible: pointCloud.visible,
        saved: pointCloud.saved
      };

      let newPointCloud;

      if (pointCloud.type === AssetType.LAS) {
        newPointCloud = new GenericPointCloud(this);
      } else if (pointCloud.type === AssetType.E57Points) {
        newPointCloud = new GenericPointCloud(this);
      } else if (pointCloud.type === AssetType.Encompass) {
        newPointCloud = new EncompassPointCloud(this);
      } else if (pointCloud.type === AssetType.Potree) {
        newPointCloud = new PotreePointCloud(this);
      }

      if (!newPointCloud) return;
      newPointCloud.load(cloudDetails);
    });

    // Toggle proper visibility for each cloud
    pointClouds.forEach(pointCloud => {
      this.toggleVisibility(pointCloud.id, pointCloud.visible);
    });

    const numClouds = this.clouds.length;

    return {numClouds, numCloudsAdded, numCloudsRemoved};
  }

  initScene() {
    this.scene = new Scene();
    this.scene.add(new AmbientLight(0xFFFFFF));
  }

  getByID(id) {
    return this.clouds.find(pointCloud => pointCloud.identifier === id);
  }

  doesCloudExist(identifier: string): boolean {
    return this.clouds.filter(cloud => cloud.identifier === identifier).length > 0;
  }

  addPointCloud(cloud: GenericPointCloud | StreamablePointCloud) {
    this.clouds.push(cloud);
    this.updateMeasureList();
  }

  deletePointCloud(identifier) {
    const clouds = new Set(this.clouds);
    const toRemove = this.clouds.filter(cloud => cloud.identifier === identifier);

    toRemove.forEach(cloud => {
      cloud.dispose();
      clouds.delete(cloud);
    });

    this.clouds = [...clouds];
    this.updateMinimapExtent();
    this.updateMeasureList();
  }

  updateMeasureList() {
    const measurements = this.controls.measurements;
    measurements.refreshMeasurementList();
  }

  toggleVisibility(identifier, state) {
    this.clouds
      .filter(cloud => cloud.identifier === identifier)
      .forEach(cloud => {
        cloud.setVisibility(state);
      });
  }

  updateClamping(key, data) {
    if (data.max <= data.min) {
      return;
    }

    let value = new Vector2(data.min, data.max);
    this.materialDefaults[key] = value;
    this.clouds.forEach(cloud => {
      cloud.materials.forEach(material => {
        material[key] = value;
      });
    });
  }

  setMaxDistance(distance: number) {
    const value = (distance > 999) ? 1e10 : distance;
    this.materialDefaults.distance = value;
    this.clouds.forEach(pointCloud => {
      pointCloud.materials.forEach(material => {
        material.distance = value;
      });
    });
  }

  setVisibleClassifications(classifications) {
    // Update defaults for new clouds
    classifications.forEach(classification => {
      const {id, visible} = classification;
      this.materialDefaults.classifications[id] = visible;
    });

    // Update values in existing clouds
    this.clouds.forEach(cloud => {
      cloud.materials.forEach(material => {
        material.updateVisibleClassifications(
          this.materialDefaults);
      });

      cloud.picker.needsUpdate = true;
    });
  }

  setPointCloudScale(size) {
    this.materialDefaults.size = size;
    this.clouds.forEach(pointCloud => {
      pointCloud.materials.forEach(material => {
        material.size = size;
        material.needsUpdate = true;
      });
    });
  }

  setPointCloudMinimum(size) {
    this.materialDefaults.minSize = size;
    this.clouds.forEach(pointCloud => {
      pointCloud.materials.forEach(material => {
        material.minSize = size;
        material.needsUpdate = true;
      });
    });
  }

  updateMinimapExtent() {
    const map = this.minimap.map;
    if (this.layer) {
      map.removeLayer(this.layer);
    }

    if (!LocalScene.initialized) return;

    let boundingBox = this.combinedBoundingBox;
    if (!boundingBox) return;

    // Aerial view layer
    this.layer = boundsToMapLayer(boundingBox);
    map.addLayer(this.layer);
  }

  updateMaterialValues(cloud: PointCloud, material: PointCloudMaterial) {
    let controls = this.viewer.controls;
    let camera = this.viewer.camera;
    let target = controls.getCameraTarget();

    material.isOctreeCloud = !(cloud instanceof GenericPointCloud);
    material.isPotreeCloud = cloud instanceof PotreePointCloud;
    material.currentCameraTarget = target;
    material.screenHeight = this.viewer.height;
    material.screenwidth = this.viewer.width;
    material.fov = MathUtils.degToRad(camera.fov);

    if (cloud instanceof StreamablePointCloud) {
      material.octreeSpacing = cloud.spacing;
      material.octreeSize = parseFloat(cloud.root.size);
      material.octreeCorner = new Vector3()
        .add(cloud.root.center)
        .subScalar(cloud.root.size / 2.0);
    }

    let intensityRange = cloud.intensityRange;
    if (intensityRange) {
      let intensity = new Vector2(intensityRange.min,
        intensityRange.max);
      material.intensityRange = intensity;
    }

    let maxColor = cloud.maxColor;
    if (maxColor) {
      let maxValue = closestPowerOfTwo(maxColor);
      material.rgbMax = maxValue;
    }

    let tightBoundingBox = this.combinedBoundingBox;
    if (tightBoundingBox) {
      let height = new Vector2(tightBoundingBox.min.z,
        tightBoundingBox.max.z);
      material.heightRange = height;
    }
  }

  updateCloudSampling() {
    let percent = this.visiblePointsTarget / this.numLoadedPoints;
    percent = Math.min(1.0, percent);

    this.clouds.forEach(cloud => {
      if (!(cloud instanceof GenericPointCloud)) return;
      if (!cloud.visible || !cloud.root) return;

      cloud.root.children.forEach(child => {
        if (!child.geometry) return;
        let numPoints = child.numPoints;
        let sampledPoints = Math.floor(numPoints * percent);
        child.geometry.setDrawRange(0, sampledPoints);
      });
    });
  }

  updateCrossSection(start, end, width) {
    this.clouds.forEach(pointcloud => {
      let material = pointcloud.materialScene;
      material.crossSectionStart = start;
      material.crossSectionEnd = end;
      material.crossSectionWidth = width;
      material.needsUpdate = true;
    });
  }

  calculatePointsVisible() {
    let visibleNodes = this.scene.children
      .filter(x => x instanceof Points)
      .filter(x => x.visible) as Points[];

    this.numVisiblePoints = 0;
    this.numSceneNodesVisible = visibleNodes.length;

    visibleNodes.forEach(node => {
      let geometry = node.geometry as BufferGeometry;
      let numPoints = geometry.attributes.position.count;
      let numVisible = Math.min(numPoints, geometry.drawRange.count);
      this.numVisiblePoints += numVisible;
    });
  }

  updateVisibilityTexture(cloud: StreamablePointCloud, material) {
    // Static sizing, exit the function
    if (!material.adaptiveSize) return;

    let nodes = cloud.getVisibleNodes();
    let texture = material.visibleNodesTexture;
    let data = texture.image.data;

    let nodeMap = new Map();
    let visibleNodeTextureOffsets = new Map();
    let offsetsToChild = new Array(nodes.length).fill(Infinity);

    // Clear previous values
    for (let i = 0; i < data.length; i++) {
      data[i] = 0;
    }

    nodes.forEach((node, nodeIndex) => {
      nodeMap.set(node.name, node);
      visibleNodeTextureOffsets.set(node, nodeIndex);

      if (nodeIndex > 0) {
        let index = parseInt(node.name.slice(-1));
        let parentName = node.name.slice(0, -1);
        let parent = nodeMap.get(parentName);
        let parentOffset = visibleNodeTextureOffsets.get(parent);

        let parentOffsetToChild = (nodeIndex - parentOffset);

        offsetsToChild[parentOffset] = Math.min(offsetsToChild[parentOffset], parentOffsetToChild);

        data[parentOffset * 4 + 0] = data[parentOffset * 4 + 0] | (1 << index);
        data[parentOffset * 4 + 1] = (offsetsToChild[parentOffset] >> 8);
        data[parentOffset * 4 + 2] = (offsetsToChild[parentOffset] % 256);
      }

      let lodOffset = 0.0;
      let density = node.density;

      if (typeof density === "number" && !Number.isNaN(density)) {
        lodOffset = Math.log2(density) / 2 - 1.5;
      }

      let offsetUint8 = (lodOffset + 10) * 10;
      data[nodeIndex * 4 + 3] = offsetUint8;
    });

    texture.needsUpdate = true;
  }

  updateClipBoxTexture(material: PointCloudMaterial) {
    let pointMarkup = this.controls.pointMarkup;
    let texture = material.clipBoxTexture;

    let clippingBoxes = pointMarkup.clippingBoxes;
    let numClipBoxes = clippingBoxes.length;

    clippingBoxes.forEach((clippingBox, index) => {
      if (index >= texture.image.width) return;

      const position = clippingBox.position;
      const scale = clippingBox.scale;
      const hidePoints = clippingBox.hidePoints;
      const updateClassification = clippingBox.updateClassification;
      const newClassification = clippingBox.newClassification;
      const ignoredClassification = clippingBox.ignoredClassification;
      const inverse = getEulerInverse(clippingBox.rotation);

      // position
      assignPackedData(texture, index, 0, position.x);
      assignPackedData(texture, index, 1, position.y);
      assignPackedData(texture, index, 2, position.z);

      // scale
      assignPackedData(texture, index, 3, scale.x);
      assignPackedData(texture, index, 4, scale.y);
      assignPackedData(texture, index, 5, scale.z);

      // rotation
      assignPackedData(texture, index, 6, inverse.x);
      assignPackedData(texture, index, 7, inverse.y);
      assignPackedData(texture, index, 8, inverse.z);

      assignPackedData(texture, index, 9, hidePoints);
      assignPackedData(texture, index, 10, updateClassification);
      assignPackedData(texture, index, 11, newClassification);
      assignPackedData(texture, index, 12, ignoredClassification);
    });

    if (numClipBoxes > 0) {
      texture.needsUpdate = true;
    }

    material.updateNumClippingBoxes(numClipBoxes);
  }

  updateAreaTriangleTexture(material) {
    let texture = material.areaTriangleTexture;

    const controller = this.controls.measurements;
    let areaGeometry = controller.getAreaGeometry();

    let numAreaTriangles = 0;
    areaGeometry.forEach(data => {
      data.indices.forEach(triangleIndex => {
        let index = numAreaTriangles;
        if (index >= texture.image.width) {
          return;
        }

        // volume status
        assignPackedData(texture, index, 0, data.volumeStatus);

        // vertex #1
        let vertex1 = data.vertices[triangleIndex[0]];
        assignPackedData(texture, index, 1, vertex1.x);
        assignPackedData(texture, index, 2, vertex1.y);
        assignPackedData(texture, index, 3, vertex1.z);

        // vertex #2
        let vertex2 = data.vertices[triangleIndex[1]];
        assignPackedData(texture, index, 4, vertex2.x);
        assignPackedData(texture, index, 5, vertex2.y);
        assignPackedData(texture, index, 6, vertex2.z);

        // vertex #3
        let vertex3 = data.vertices[triangleIndex[2]];
        assignPackedData(texture, index, 7, vertex3.x);
        assignPackedData(texture, index, 8, vertex3.y);
        assignPackedData(texture, index, 9, vertex3.z);

        numAreaTriangles += 1;
      });
    });

    if (numAreaTriangles > 0) {
      texture.needsUpdate = true;
    }

    material.updateNumAreaTriangles(numAreaTriangles);
  }

  pointsInRadius(position, size) {
    var points = [];

    this.clouds.forEach(cloud => {
      // Grab valid nodes
      let nodes = cloud.getVisibleNodes(node => {
        let dx = Math.abs(node.center.x - position.x);
        let dy = Math.abs(node.center.y - position.y);

        return !((dx > node.size) || (dy > node.size));
      });

      // Grab valid points
      nodes.forEach(node => {
        let geometry = node.pointsScene.geometry as BufferGeometry;
        let positions = geometry.attributes.position;

        for (var i = 0; i < positions.count; i++) {
          let x = positions.getX(i) + node.pointsScene.position.x;
          let y = positions.getY(i) + node.pointsScene.position.y;
          let z = positions.getZ(i) + node.pointsScene.position.z;

          let dx = Math.abs(x - position.x);
          let dy = Math.abs(y - position.y);

          if ((dx > size) || (dy > size)) {
            continue;
          }

          let point = new Vector3(x, y, z);
          points.push(point);
        }
      });
    });

    return points;
  }

  pointsInPolygon(points, vertices) {
    let pointsInPolygon = [];

    points.forEach(point => {
      if (pointInPolygon(point, vertices)) {
        pointsInPolygon.push(point);
      }
    });

    return pointsInPolygon;
  }

  getSampledPoints(points, highest, keyfunc) {
    let samples = {};
    points.forEach(point => {
      let key = keyfunc(point);
      if (!(key in samples)) {
        samples[key] = [];
      }

      samples[key].push(point);
    });

    let dtmPoints = [];
    let keys = Object.keys(samples);
    keys.forEach(key => {
      let data = samples[key];
      data.sort((a, b) => (a.z > b.z) ? 1 : -1);

      let sampled = highest
        ? data[data.length - 1]
        : data[0];

      dtmPoints.push(sampled);
    });

    return dtmPoints;
  }

  getCameraFrustum() {
    // Calculate current view frustum
    const camera = this.viewer.camera;
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld(true);
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();

    let cameraViewProjectionMatrix = new Matrix4();
    cameraViewProjectionMatrix.multiplyMatrices(
      camera.projectionMatrix,
      camera.matrixWorldInverse
    );

    let frustum = new Frustum();
    frustum.setFromProjectionMatrix(cameraViewProjectionMatrix);
    return frustum;
  }

  manageNodeCache() {
    let stack = [];
    let unusedNodes = [];
    let numVisible = 0;
    let numHidden = 0;

    this.streamingPointClouds.forEach(cloud => {
      if (!cloud.root) return;
      stack.push(cloud.root);
    });

    while (stack.length > 0) {
      let object = stack.shift();

      if (object.visible) {
        numVisible++;
      } else if (object.loaded) {
        numHidden++;
        unusedNodes.push(object);
      } else {
        continue;
      }

      object.children.forEach(child => {
        stack.push(child);
      });
    }

    const numberOfNodes = numVisible + numHidden;
    if (numberOfNodes < this.nodeCacheSize) return;

    // We delete from our list of unused nodes plus an extra percentage of the total
    let numberToDelete = numberOfNodes - 0.8 * this.nodeCacheSize;
    numberToDelete = Math.min(numberToDelete, numHidden);
    if (numberToDelete === 0) return;

    // Delete unused nodes, starting at the highest detail level we have
    unusedNodes.reverse();
    unusedNodes = unusedNodes.slice(0, numberToDelete);
    unusedNodes.forEach(node => {
      node.dispose();
    });
  }

  launchNewExecutables() {
    let numLoading = 0;
    let cloudsToExecute = [];

    this.genericPointClouds.forEach(cloud => {
      if ((!cloud.canReadData) || (!cloud.visible) || (cloud.loaded)) return;
      if (numLoading >= this.maxUnloadedClouds) return;

      numLoading += 1;

      if (!cloud.exeRunning) {
        let timeNow = performance.now();
        let timeSinceLaunch = timeNow - this.lastExeLaunched;
        if (timeSinceLaunch > this.exeLaunchTimeout) {
          this.lastExeLaunched = timeNow;
          cloudsToExecute.push(cloud);
        }
      }
    });

    // Launch exe for new
    if (cloudsToExecute.length > 0) {
      let cloud = cloudsToExecute.shift();
      cloud.launchExecutable();
      return;
    }
  }

  loadUnloadedGeometry() {
    const nodesToLoad = this.unloadedGeometry
      .slice(0, this.maxUnloadedNodes);

    nodesToLoad.forEach(node => {
      node.load();
    });
  }

  updateGenericPointClouds(frustum: Frustum) {
    if (isStaticSite || !this.shouldUpdate) return;

    this.genericPointClouds.forEach(cloud => {
      // Hide all nodes
      cloud.hideDescendants();
    });

    if (!this.cameraList.waitForCameras) {
      // Load visible nodes
      this.calculateGenericVisibility(frustum);
    }
  }

  updateStreamingPointClouds(frustum: Frustum) {
    if (!this.shouldUpdate) return;

    this.streamingPointClouds.forEach(cloud => {
      // Hide all nodes
      cloud.hideDescendants();
    });

    // Load visible nodes
    this.calculateStreamingVisibility(frustum);

    // Remove unused nodes
    this.manageNodeCache();
  }

  /** @private */
  calculateGenericVisibility(frustum: Frustum) {
    if (this.cameraList.waitForCameras) return;

    let nodes = [];

    this.genericPointClouds.forEach(cloud => {
      if (!cloud.visible || !cloud.root) return;

      const newNodes = [cloud.root, ...cloud.root.children];
      nodes = [...nodes, ...newNodes];
    });

    let numLoadedPoints = 0;

    nodes.forEach(node => {
      // Ignore nodes based on our view frustrum
      if (!frustum.intersectsBox(node.boundingBox)) {
        return;
      }

      // Add node to scene if data is loaded, or else add to unloaded list
      if (node.isGeometryNode && (!node.parent || node.parent.isTreeNode)) {
        if (node.loaded && this.loadedToGPUThisFrame < this.maxloadedToGPUPerFrame) {
          node.addToScene();
          this.loadedToGPUThisFrame++;
        } else {
          this.unloadedGeometry.push(node);
        }
      }

      // Update node visibility for loaded nodes
      if (node.isTreeNode) {
        node.setVisibility(true, this.boundsVisible);
        numLoadedPoints += node.numPoints;
      }
    });

    this.numLoadedPoints += numLoadedPoints;
  }

  /** @private */
  calculateStreamingVisibility(frustum: Frustum) {
    if (this.cameraList.waitForCameras) return;

    const priorityQueue = new BinaryHeap(x => 1 / x.weight);

    this.streamingPointClouds.forEach(cloud => {
      if (!cloud.visible || !cloud.root) return;

      priorityQueue.push({
        node: cloud.root,
        weight: Number.MAX_VALUE
      });
    });

    let numLoadedPoints = 0;

    const maxAllowedPoints = this.visiblePointsTarget;
    const maxNodeDistance = this.materialDefaults.distance;
    const controls = this.viewer.controls;
    const measurePoint = controls.getCameraTarget();
    const cameraPosition = this.viewer.camera.position;

    const screenHeight = this.viewer.height;
    const fov = MathUtils.degToRad(Math.max(this.viewer.camera.fov, 45));
    const minNodeSize = this.viewer.height * this.minPixelPercent;

    while (priorityQueue.size > 0) {
      const element = priorityQueue.pop();
      const node = element.node as StreamablePointCloudNode;
      const newPointsTarget = numLoadedPoints + node.numPoints;

      // Check to see if the point limit will be exceeded
      if (newPointsTarget >= maxAllowedPoints) {
        continue;
      }

      // Ignore nodes based on our view frustrum
      if (!frustum.intersectsBox(node.boundingBox)) {
        continue;
      }

      // Ignore nodes based on distance slider
      let bounds2d = new Vector2(node.center.x, node.center.y);
      let camera2d = new Vector2(measurePoint.x, measurePoint.y);
      let nodeDistance = bounds2d.distanceTo(camera2d);
      if (nodeDistance > (node.radius + maxNodeDistance)) {
        continue;
      }

      if (node.isGeometryNode && (!node.parent || node.parent.isTreeNode)) {
        if (node.loaded && this.loadedToGPUThisFrame < this.maxloadedToGPUPerFrame) {
          node.addToScene();
          this.loadedToGPUThisFrame++;
        } else {
          this.unloadedGeometry.push(node);
        }
      }

      if (node.isTreeNode) {
        node.setVisibility(true, this.boundsVisible);
        numLoadedPoints += node.numPoints;
      }

      node.children.forEach((child: StreamablePointCloudNode) => {
        const childDistance = child.center.distanceTo(cameraPosition);
        const insideSphere = childDistance < child.radius;

        // Pixel size based filtering
        const projFactor = getProjFactor(fov, childDistance, screenHeight);
        const nodeScreenSize = child.radius * projFactor;
        if (nodeScreenSize < minNodeSize) return;

        const weight = insideSphere
          ? Number.MAX_VALUE
          : nodeScreenSize;

        priorityQueue.push({
          node: child,
          weight: weight
        });
      });
    }

    this.numLoadedPoints += numLoadedPoints;
  }

  updateMaterials() {
    this.clouds.forEach(cloud => {
      cloud.materials.forEach(material => {
        if (!material || !cloud.root) return;

        this.updateMaterialValues(cloud, material);

        if (!material.gpuPicker) {
          this.updateAreaTriangleTexture(material);
          this.updateClipBoxTexture(material);
        }

        if (cloud instanceof StreamablePointCloud) {
          this.updateVisibilityTexture(cloud, material);
        }
      });
    });
  }

  update() {
    this.numLoadedPoints = 0;
    this.loadedToGPUThisFrame = 0;
    this.unloadedGeometry = [];

    const frustum = this.getCameraFrustum();

    // Update clouds based on current view
    this.updateGenericPointClouds(frustum);
    this.updateStreamingPointClouds(frustum);

    // Load unloaded geometry data
    this.loadUnloadedGeometry();

    // Create python exectutables for generic poinclouds
    this.launchNewExecutables();

    // Update material and texture data
    this.updateMaterials();

    // Sample raw pointclouds based on total points loaded
    this.updateCloudSampling();

    this.calculatePointsVisible();
  }
}